import { EditOutlined } from '@mui/icons-material'
import { Box, Chip, Stack, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import CustomButton from '@/components/inputs/CustomButton'
import { PrequalificationRouteInformation } from '@/features/prequalification/constants'
import type { ResourceSetMember } from '@/features/prequalification/localControl/types'
import { ResourceRouteInformation } from '@/features/resource/constants'

type ResourceSetResourcesSummaryProps = {
  resourceSetId: string
  resources: ResourceSetMember[]
}

/**
 * The detail page's read-only view of a resource set's resources: the set as chips, plus
 * an "Edit resources" action that opens the full-page editor. All add/remove gating
 * lives on that editor, so this surface is purely informational.
 */
function ResourceSetResourcesSummary({ resourceSetId, resources }: Readonly<ResourceSetResourcesSummaryProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <Stack sx={{ alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 520, maxHeight: 200, overflowY: 'auto' }}>
        {resources.map((resource) => {
          const hasName = resource.name !== resource.resourceId
          const label = hasName ? resource.name : t('prequalifications.resource_set.resources.unnamed')
          return (
            <Tooltip
              key={resource.resourceId}
              title={t('prequalifications.resource_set.resources.open_resource', { resourceName: label })}
            >
              <Chip
                component={RouterLink}
                label={label}
                rel="noopener noreferrer"
                size="small"
                sx={{
                  maxWidth: 280,
                  color: 'text.secondary',
                  fontStyle: hasName ? 'normal' : 'italic',
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
                }}
                target="_blank"
                to={ResourceRouteInformation.RESOURCE_DETAILS.navigationPath(resource.resourceId)}
                variant="outlined"
              />
            </Tooltip>
          )
        })}
      </Box>

      <CustomButton
        size="small"
        startIcon={<EditOutlined />}
        variant="outlined"
        onClick={() => navigate(PrequalificationRouteInformation.EDIT_RESOURCES.navigationPath(resourceSetId))}
      >
        {t('prequalifications.resource_set.edit_resources.title')}
      </CustomButton>
    </Stack>
  )
}

export default ResourceSetResourcesSummary
